import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileMinus2, RefreshCw, Send } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import { colorDeLaSituacion } from './estadosDeFactura';

interface Nota {
  id: number;
  numero: string | null;
  numeroDeLaFactura: string | null;
  cliente: string;
  identificacion: string;
  tipo: string;
  motivo: string;
  total: number;
  situacionSri: string;
  mensajeSri: string | null;
  fechaEmision: string;
}

export function NotasEnElProceso({ punto }: { punto: number | null }) {
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);
  const [enCurso, setEnCurso] = useState<number | null>(null);
  const clienteDeConsultas = useQueryClient();

  const notas = useQuery({
    queryKey: ['notas-pendientes', pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Nota>>('/notas-credito', {
        query: { situacionSri: 'pendientes', pagina, tamanio },
      }),
  });

  function refrescar() {
    setEnCurso(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['notas-pendientes'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['notas-credito'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['facturas'] });
  }

  const preguntar = useMutation({
    mutationFn: (id: number) =>
      api.post<{ numero: string; situacion: string; mensaje: string }>(
        `/notas-credito/${id}/reconsultar`,
      ),
    onSuccess: (r) => {
      if (r.situacion === 'autorizada') {
        notificar.exito(`${r.numero} · AUTORIZADA · LA FACTURA QUEDA ACREDITADA`);
      } else {
        notificar.exito(`${r.numero} · ${r.mensaje}`);
      }
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo preguntar'),
  });

  const reintentar = useMutation({
    mutationFn: (id: number) =>
      api.post<{ numero: string; situacion: string; mensaje: string }>(
        `/notas-credito/${id}/reintentar?puntoEmision=${punto}`,
      ),
    onSuccess: (r) => {
      if (r.situacion === 'autorizada') {
        notificar.exito(`${r.numero} · AUTORIZADA`);
      } else {
        notificar.error(`${r.numero} · ${r.mensaje}`);
      }
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo volver a mandar'),
  });

  const columnas: Columna<Nota>[] = [
    {
      clave: 'numero',
      encabezado: 'Nº nota',
      contenido: (n) => (
        <span className="block">
          <span className="block font-mono text-xs font-semibold text-armazon-700">
            {n.numero ?? <span className="text-armazon-400">SIN EMITIR</span>}
          </span>
          <span className="block text-[10px] uppercase text-armazon-400">
            {n.tipo === 'total' ? 'Total' : 'Parcial'}
          </span>
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (n) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{n.cliente}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{n.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'acredita',
      encabezado: 'Acredita a',
      contenido: (n) => (
        <span className="font-mono text-xs text-armazon-600">{n.numeroDeLaFactura}</span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Importe',
      alinear: 'derecha',
      contenido: (n) => <span className="importe">${n.total.toFixed(2)}</span>,
    },
    {
      clave: 'situacion',
      encabezado: 'Ante el SRI',
      alinear: 'centro',
      contenido: (n) => (
        <span
          className={`text-[11px] font-semibold uppercase ${colorDeLaSituacion(n.situacionSri)}`}
        >
          {n.situacionSri}
        </span>
      ),
    },
    {
      clave: 'mensaje',
      encabezado: 'Qué dijo el SRI',

      contenido: (n) => (
        <span className="block max-w-md text-[11px] leading-relaxed text-red-700">
          {n.mensajeSri ?? '—'}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'derecha',
      contenido: (n) => (
        <span className="flex justify-end gap-1">
          {n.numero && (
            <BotonDeFila
              icono={RefreshCw}
              titulo="Preguntar al SRI por esta nota"
              alPulsar={() => {
                setEnCurso(n.id);
                preguntar.mutate(n.id);
              }}
              ocupado={enCurso === n.id}
            />
          )}

          {n.situacionSri === 'rechazada' && (
            <BotonDeFila
              icono={Send}
              titulo={
                punto === null
                  ? 'Elija primero por qué caja sale'
                  : 'Volver a mandarla, con numeración nueva'
              }
              alPulsar={() => {
                setEnCurso(n.id);
                reintentar.mutate(n.id);
              }}
              ocupado={enCurso === n.id || punto === null}
            />
          )}
        </span>
      ),
    },
  ];

  return (
    <Tabla
      datos={notas.data}
      cargando={notas.isLoading}
      columnas={columnas}
      tamanio={tamanio}
      alCambiarPagina={setPagina}
      alCambiarTamanio={(nuevo) => {
        setTamanio(nuevo);
        setPagina(0);
      }}
      iconoVacio={FileMinus2}
      mensajeVacio="Ninguna nota de crédito pendiente: todas autorizadas"
      entidad="PENDIENTES"
    />
  );
}
